/**
 * DAFNE Pattern Analysis Engine
 *
 * Analyzes CGM and diary data to detect patterns and provide
 * DAFNE-aligned recommendations for insulin adjustments.
 */

import { subDays, startOfDay, endOfDay } from "date-fns";
import type { CgmReading, LogEntry, PeriodTag } from "../types";
import { getQATotal, calculateIOB, PERIOD_TAG_INFO } from "../types";

// Pattern severity levels
export type PatternSeverity = "info" | "warning" | "action";

// Pattern types
export type PatternType =
  | "dawn"
  | "post-meal"
  | "hypo-pattern"
  | "hyper-pattern"
  | "exercise"
  | "sick"
  | "nighttime"
  | "ratio"
  | "basal"
  | "correction"
  | "tir"
  | "variability";

// Pattern result structure
export interface PatternResult {
  type: PatternType;
  severity: PatternSeverity;
  title: string;
  detail: string;
  dafneRecommendation: string;
  affectedPeriods?: PeriodTag[];
  confidence: number; // 0-1 based on data quality
  data?: {
    avgBg?: number;
    count?: number;
    times?: string[];
    tir?: number;
  };
}

// Prediction types
export type PredictionType =
  | "bg-trajectory"
  | "hypo-risk"
  | "hyper-risk"
  | "stacking";

// Prediction result structure
export interface PredictionResult {
  type: PredictionType;
  timeframe: string;
  likelihood: "low" | "medium" | "high";
  basedOn: string;
  action?: string;
  currentIob?: number;
  predictedBg?: number;
}

// Analysis options
export interface AnalysisOptions {
  cgmReadings: CgmReading[];
  entries: LogEntry[];
  daysToAnalyze?: number; // Default 7
  targetLow?: number; // Default 4
  targetHigh?: number; // Default 10
}

// Time period definitions for analysis
const TIME_PERIODS = {
  dawn: { start: 5, end: 8, label: "Dawn (5-8 AM)" },
  morning: { start: 8, end: 12, label: "Morning (8 AM-12 PM)" },
  afternoon: { start: 12, end: 17, label: "Afternoon (12-5 PM)" },
  evening: { start: 17, end: 21, label: "Evening (5-9 PM)" },
  night: { start: 21, end: 24, label: "Night (9 PM-12 AM)" },
  overnight: { start: 0, end: 5, label: "Overnight (12-5 AM)" },
};

// Post-meal windows (hours after meal period start)
const POST_MEAL_WINDOWS = {
  B: { peakStart: 1, peakEnd: 3, label: "Post-Breakfast" },
  L: { peakStart: 1, peakEnd: 3, label: "Post-Lunch" },
  EM: { peakStart: 1, peakEnd: 3, label: "Post-Evening Meal" },
};

/**
 * Get timestamp as number (handles both Date and number)
 */
function getTimestamp(ts: Date | number): number {
  return typeof ts === "number" ? ts : ts.getTime();
}

/**
 * Filter readings by time period
 */
function filterByTimePeriod(
  readings: CgmReading[],
  startHour: number,
  endHour: number,
): CgmReading[] {
  return readings.filter((r) => {
    const hour = new Date(getTimestamp(r.timestamp)).getHours();
    if (startHour < endHour) {
      return hour >= startHour && hour < endHour;
    } else {
      // Handles overnight wrap (e.g., 21-5)
      return hour >= startHour || hour < endHour;
    }
  });
}

/**
 * Calculate average glucose from readings
 */
function calculateAvg(readings: CgmReading[]): number {
  if (readings.length === 0) return 0;
  return readings.reduce((sum, r) => sum + r.glucose, 0) / readings.length;
}

/**
 * Calculate standard deviation of glucose
 */
function calculateStdDev(readings: CgmReading[]): number {
  if (readings.length < 2) return 0;
  const avg = calculateAvg(readings);
  const squaredDiffs = readings.map((r) => Math.pow(r.glucose - avg, 2));
  return Math.sqrt(
    squaredDiffs.reduce((sum, d) => sum + d, 0) / readings.length,
  );
}

/**
 * Calculate Time in Range (TIR)
 */
function calculateTir(
  readings: CgmReading[],
  low: number,
  high: number,
): number {
  if (readings.length === 0) return 0;
  const inRange = readings.filter(
    (r) => r.glucose >= low && r.glucose <= high,
  ).length;
  return Math.round((inRange / readings.length) * 100);
}

/**
 * Count hypos (readings below threshold)
 */
function countHypos(readings: CgmReading[], threshold = 4): number {
  return readings.filter((r) => r.glucose < threshold).length;
}

/**
 * Count highs (readings above threshold)
 */
function countHighs(readings: CgmReading[], threshold = 10): number {
  return readings.filter((r) => r.glucose > threshold).length;
}

/**
 * Episode: A continuous period where readings are above/below a threshold
 */
interface Episode {
  startTime: number;
  endTime: number;
  readings: CgmReading[];
  avgGlucose: number;
  minGlucose: number;
  maxGlucose: number;
  durationMinutes: number;
}

/**
 * Group consecutive readings into episodes based on threshold
 * Gap tolerance: readings up to 30 minutes apart are considered the same episode
 * For hypos (direction="below"), single readings also count as episodes
 */
function groupIntoEpisodes(
  readings: CgmReading[],
  threshold: number,
  direction: "above" | "below",
): Episode[] {
  const sorted = [...readings].sort(
    (a, b) => getTimestamp(a.timestamp) - getTimestamp(b.timestamp),
  );

  const filtered = sorted.filter((r) =>
    direction === "above" ? r.glucose > threshold : r.glucose < threshold,
  );

  console.log(
    `[groupIntoEpisodes] threshold=${threshold}, direction=${direction}`,
  );
  console.log(
    `[groupIntoEpisodes] Total readings: ${readings.length}, matching threshold: ${filtered.length}`,
  );

  if (filtered.length === 0) {
    console.log(`[groupIntoEpisodes] No matching readings found`);
    return [];
  }

  // Debug: show first few matching readings
  console.log(`[groupIntoEpisodes] First 5 matching readings:`);
  for (let i = 0; i < Math.min(5, filtered.length); i++) {
    const r = filtered[i];
    console.log(
      `  ${new Date(getTimestamp(r.timestamp)).toISOString()} - ${r.glucose} mmol/L`,
    );
  }

  const episodes: Episode[] = [];
  let currentEpisode: CgmReading[] = [filtered[0]];
  const gapTolerance = 30 * 60 * 1000; // 30 minutes (2 CGM readings typically)

  for (let i = 1; i < filtered.length; i++) {
    const prevTime = getTimestamp(filtered[i - 1].timestamp);
    const currTime = getTimestamp(filtered[i].timestamp);

    if (currTime - prevTime <= gapTolerance) {
      currentEpisode.push(filtered[i]);
    } else {
      // Finalize current episode
      // For hypos, even single readings count (brief hypo is still a hypo)
      // For highs, require at least 2 readings (sustained high)
      const minReadings = direction === "below" ? 1 : 2;
      if (currentEpisode.length >= minReadings) {
        episodes.push(createEpisode(currentEpisode));
      }
      currentEpisode = [filtered[i]];
    }
  }

  // Don't forget the last episode
  const minReadings = direction === "below" ? 1 : 2;
  if (currentEpisode.length >= minReadings) {
    episodes.push(createEpisode(currentEpisode));
  }

  console.log(`[groupIntoEpisodes] Created ${episodes.length} episodes`);
  for (let i = 0; i < Math.min(5, episodes.length); i++) {
    const ep = episodes[i];
    console.log(
      `  Episode ${i + 1}: ${new Date(ep.startTime).toISOString()} - ${ep.readings.length} readings, ${ep.durationMinutes} min, avg=${ep.avgGlucose.toFixed(1)}`,
    );
  }
  if (episodes.length > 5) {
    console.log(`  ... and ${episodes.length - 5} more episodes`);
  }

  return episodes;
}

function createEpisode(readings: CgmReading[]): Episode {
  const sorted = readings.sort(
    (a, b) => getTimestamp(a.timestamp) - getTimestamp(b.timestamp),
  );
  const startTime = getTimestamp(sorted[0].timestamp);
  const endTime = getTimestamp(sorted[sorted.length - 1].timestamp);

  return {
    startTime,
    endTime,
    readings: sorted,
    avgGlucose: calculateAvg(sorted),
    minGlucose: Math.min(...sorted.map((r) => r.glucose)),
    maxGlucose: Math.max(...sorted.map((r) => r.glucose)),
    durationMinutes: Math.round((endTime - startTime) / (60 * 1000)),
  };
}

/**
 * Get days affected by episodes
 */
function getDaysWithEpisodes(episodes: Episode[]): Set<string> {
  const days = new Set<string>();
  for (const ep of episodes) {
    const date = new Date(ep.startTime);
    days.add(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`);
  }
  return days;
}

/**
 * Get the actual number of unique days in the readings data
 */
function getActualDaysInData(readings: CgmReading[]): number {
  const days = new Set<string>();
  for (const r of readings) {
    const date = new Date(getTimestamp(r.timestamp));
    days.add(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`);
  }
  return days.size;
}

/**
 * Find typical time range when episodes occur
 */
function findTypicalTimeRange(episodes: Episode[]): {
  startHour: number;
  endHour: number;
  label: string;
} | null {
  if (episodes.length === 0) return null;

  // Count which hours have the most episode starts
  const hourCounts: Map<number, number> = new Map();
  for (const ep of episodes) {
    const hour = new Date(ep.startTime).getHours();
    hourCounts.set(hour, (hourCounts.get(hour) || 0) + 1);
  }

  // Find peak hour and build a range around it
  let peakHour = 0;
  let peakCount = 0;
  for (const [hour, count] of hourCounts) {
    if (count > peakCount) {
      peakHour = hour;
      peakCount = count;
    }
  }

  // Expand to find contiguous range where episodes occur
  let startHour = peakHour;
  let endHour = peakHour;

  // Expand backwards
  for (let h = peakHour - 1; h >= 0; h--) {
    if ((hourCounts.get(h) || 0) > 0) {
      startHour = h;
    } else {
      break;
    }
  }

  // Expand forwards
  for (let h = peakHour + 1; h < 24; h++) {
    if ((hourCounts.get(h) || 0) > 0) {
      endHour = h;
    } else {
      break;
    }
  }

  const formatHr = (h: number) => {
    if (h === 0) return "12 AM";
    if (h < 12) return `${h} AM`;
    if (h === 12) return "12 PM";
    return `${h - 12} PM`;
  };

  return {
    startHour,
    endHour: endHour + 1, // Make it inclusive
    label:
      startHour === endHour
        ? `around ${formatHr(startHour)}`
        : `${formatHr(startHour)}-${formatHr(endHour + 1)}`,
  };
}

/**
 * Format duration for display
 */
function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

/**
 * Analyze dawn phenomenon
 */
function analyzeDawnPhenomenon(
  readings: CgmReading[],
  targetHigh: number,
): PatternResult | null {
  const dawnReadings = filterByTimePeriod(
    readings,
    TIME_PERIODS.dawn.start,
    TIME_PERIODS.dawn.end,
  );

  if (dawnReadings.length < 10) return null;

  const avgDawn = calculateAvg(dawnReadings);
  const dawnHighs = countHighs(dawnReadings, targetHigh);
  const dawnHighPercent = Math.round((dawnHighs / dawnReadings.length) * 100);

  // Compare to overnight readings
  const overnightReadings = filterByTimePeriod(
    readings,
    TIME_PERIODS.overnight.start,
    TIME_PERIODS.overnight.end,
  );
  const avgOvernight =
    overnightReadings.length > 0 ? calculateAvg(overnightReadings) : avgDawn;

  // Dawn phenomenon: rising BG in early morning, typically 2+ mmol/L rise
  const rise = avgDawn - avgOvernight;

  if (avgDawn > targetHigh && rise > 1.5) {
    const severity: PatternSeverity = rise > 3 ? "action" : "warning";
    return {
      type: "dawn",
      severity,
      title: "🌅 Dawn Phenomenon Detected",
      detail: `Average early morning BG is ${avgDawn.toFixed(1)} mmol/L (${dawnHighPercent}% above target). Rising ${rise.toFixed(1)} mmol/L from overnight.`,
      dafneRecommendation:
        rise > 3
          ? "Consider increasing evening basal insulin or adjusting timing. If on MDI, try taking long-acting dose at bedtime instead of dinner."
          : "Monitor pattern. May need basal adjustment if consistent over 3+ days.",
      affectedPeriods: ["B"],
      confidence: dawnReadings.length > 30 ? 0.9 : 0.7,
      data: { avgBg: avgDawn, count: dawnReadings.length },
    };
  }

  return null;
}

/**
 * Analyze post-meal patterns for a specific period
 */
function analyzePostMealPattern(
  readings: CgmReading[],
  entries: LogEntry[],
  period: "B" | "L" | "EM",
  targetHigh: number,
): PatternResult | null {
  const window = POST_MEAL_WINDOWS[period];
  const periodEntries = entries.filter((e) => e.periodTag === period);

  if (periodEntries.length < 3) return null;

  // Get post-meal readings for each entry
  let totalPostMealReadings: CgmReading[] = [];
  let spikeCount = 0;

  for (const entry of periodEntries) {
    const entryTime = getTimestamp(entry.timestamp);
    const peakStartTime = entryTime + window.peakStart * 60 * 60 * 1000;
    const peakEndTime = entryTime + window.peakEnd * 60 * 60 * 1000;

    const postMealReadings = readings.filter((r) => {
      const ts = getTimestamp(r.timestamp);
      return ts >= peakStartTime && ts <= peakEndTime;
    });

    totalPostMealReadings = [...totalPostMealReadings, ...postMealReadings];

    // Check if this meal caused a significant spike
    const avgPostMeal = calculateAvg(postMealReadings);
    if (avgPostMeal > targetHigh + 2) {
      spikeCount++;
    }
  }

  if (totalPostMealReadings.length < 5) return null;

  const avgPostMeal = calculateAvg(totalPostMealReadings);
  const spikePercent = Math.round((spikeCount / periodEntries.length) * 100);

  if (avgPostMeal > targetHigh + 1 && spikePercent > 50) {
    const periodName = PERIOD_TAG_INFO[period].label;
    return {
      type: "post-meal",
      severity: avgPostMeal > targetHigh + 3 ? "action" : "warning",
      title: `🍽️ ${window.label} Spikes`,
      detail: `${spikePercent}% of ${periodName.toLowerCase()} meals resulted in high BG. Average post-meal: ${avgPostMeal.toFixed(1)} mmol/L.`,
      dafneRecommendation: `Consider reducing the ${periodName.toLowerCase()} carb:insulin ratio (e.g., from 1:10 to 1:8). Also check carb counting accuracy for typical ${periodName.toLowerCase()} foods.`,
      affectedPeriods: [period],
      confidence: periodEntries.length > 5 ? 0.85 : 0.65,
      data: { avgBg: avgPostMeal, count: periodEntries.length },
    };
  }

  return null;
}

/**
 * Analyze hypoglycemia patterns using episode-based aggregation
 */
function analyzeHypoPatterns(
  readings: CgmReading[],
  daysAnalyzed: number,
): PatternResult | null {
  console.log(
    `[analyzeHypoPatterns] Starting with ${readings.length} readings, daysAnalyzed=${daysAnalyzed}`,
  );
  const hypoEpisodes = groupIntoEpisodes(readings, 4.0, "below");

  console.log(
    `[analyzeHypoPatterns] Found ${hypoEpisodes.length} hypo episodes`,
  );

  if (hypoEpisodes.length < 2) {
    console.log(
      `[analyzeHypoPatterns] Not enough episodes (<2), returning null`,
    );
    return null;
  }

  const daysAffected = getDaysWithEpisodes(hypoEpisodes);
  const avgDuration =
    hypoEpisodes.reduce((sum, ep) => sum + ep.durationMinutes, 0) /
    hypoEpisodes.length;
  const timeRange = findTypicalTimeRange(hypoEpisodes);

  // Determine which period is most affected
  let affectedPeriod: PeriodTag | undefined;
  if (timeRange) {
    const peakHour = timeRange.startHour;
    if (peakHour >= 0 && peakHour < 5) affectedPeriod = "3AM";
    else if (peakHour >= 5 && peakHour < 10) affectedPeriod = "B";
    else if (peakHour >= 10 && peakHour < 15) affectedPeriod = "L";
    else if (peakHour >= 15 && peakHour < 21) affectedPeriod = "EM";
    else affectedPeriod = "BT";
  }

  const severity: PatternSeverity =
    hypoEpisodes.length >= 5 || daysAffected.size >= 4 ? "action" : "warning";

  return {
    type: "hypo-pattern",
    severity,
    title: "⚠️ Recurring Hypoglycemia",
    detail: `${hypoEpisodes.length} hypo episodes across ${daysAffected.size} of ${daysAnalyzed} days. Avg duration: ${formatDuration(Math.round(avgDuration))}. Typically occurs ${timeRange?.label || "throughout the day"}.`,
    dafneRecommendation:
      affectedPeriod === "3AM"
        ? "Nighttime lows suggest basal insulin may be too high. Consider reducing evening long-acting dose by 10-20%."
        : `Consider reducing quick-acting insulin at ${affectedPeriod ? PERIOD_TAG_INFO[affectedPeriod].label.toLowerCase() : "affected meals"} by 1-2 units, or review carb counting.`,
    affectedPeriods: affectedPeriod ? [affectedPeriod] : undefined,
    confidence: 0.8,
    data: { count: hypoEpisodes.length },
  };
}

/**
 * Analyze hyperglycemia patterns using episode-based aggregation
 */
function analyzeHyperPatterns(
  readings: CgmReading[],
  targetHigh: number,
  daysAnalyzed: number,
): PatternResult | null {
  // Group into episodes of significant highs (>14 mmol/L is concerning)
  const highThreshold = targetHigh + 4;
  const hyperEpisodes = groupIntoEpisodes(readings, highThreshold, "above");

  if (hyperEpisodes.length < 2) return null;

  const daysAffected = getDaysWithEpisodes(hyperEpisodes);
  const avgDuration =
    hyperEpisodes.reduce((sum, ep) => sum + ep.durationMinutes, 0) /
    hyperEpisodes.length;
  const avgGlucose =
    hyperEpisodes.reduce((sum, ep) => sum + ep.avgGlucose, 0) /
    hyperEpisodes.length;
  const timeRange = findTypicalTimeRange(hyperEpisodes);

  // Determine which period is most affected
  let affectedPeriod: PeriodTag | undefined;
  if (timeRange) {
    const peakHour = timeRange.startHour;
    if (peakHour >= 0 && peakHour < 5) affectedPeriod = "3AM";
    else if (peakHour >= 5 && peakHour < 10) affectedPeriod = "B";
    else if (peakHour >= 10 && peakHour < 15) affectedPeriod = "L";
    else if (peakHour >= 15 && peakHour < 21) affectedPeriod = "EM";
    else affectedPeriod = "BT";
  }

  const totalTimeHigh = hyperEpisodes.reduce(
    (sum, ep) => sum + ep.durationMinutes,
    0,
  );
  const severity: PatternSeverity =
    totalTimeHigh > 300 || daysAffected.size >= 5 ? "action" : "warning"; // >5 hours total high time

  return {
    type: "hyper-pattern",
    severity,
    title: "📈 Persistent High Readings",
    detail: `${hyperEpisodes.length} extended high episodes (>${highThreshold} mmol/L) across ${daysAffected.size} of ${daysAnalyzed} days. Avg duration: ${formatDuration(Math.round(avgDuration))}. Avg BG during episodes: ${avgGlucose.toFixed(1)} mmol/L. Typically occurs ${timeRange?.label || "throughout the day"}.`,
    dafneRecommendation: `Review ${affectedPeriod ? PERIOD_TAG_INFO[affectedPeriod].label.toLowerCase() : "meal"} dosing. May need tighter ratio or increased correction factor sensitivity.`,
    affectedPeriods: affectedPeriod ? [affectedPeriod] : undefined,
    confidence: 0.75,
    data: { avgBg: avgGlucose, count: hyperEpisodes.length },
  };
}

/**
 * Analyze exercise impact
 */
function analyzeExerciseImpact(
  readings: CgmReading[],
  entries: LogEntry[],
): PatternResult | null {
  const exerciseEntries = entries.filter((e) => e.exercise);

  if (exerciseEntries.length < 2) return null;

  let postExerciseLows = 0;

  for (const entry of exerciseEntries) {
    const entryTime = getTimestamp(entry.timestamp);
    // Check 2-8 hours post-exercise window
    const postStart = entryTime + 2 * 60 * 60 * 1000;
    const postEnd = entryTime + 8 * 60 * 60 * 1000;

    const postExReadings = readings.filter((r) => {
      const ts = getTimestamp(r.timestamp);
      return ts >= postStart && ts <= postEnd;
    });

    const lowReadings = postExReadings.filter((r) => r.glucose < 4.5);
    if (lowReadings.length > 0) {
      postExerciseLows++;
    }
  }

  const lowPercent = Math.round(
    (postExerciseLows / exerciseEntries.length) * 100,
  );

  if (lowPercent > 30) {
    return {
      type: "exercise",
      severity: "action",
      title: "🏃 Post-Exercise Lows",
      detail: `${lowPercent}% of exercise sessions (${postExerciseLows}/${exerciseEntries.length}) resulted in low BG within 2-8 hours.`,
      dafneRecommendation:
        "Consider reducing quick-acting insulin by 20-30% for meals after exercise, or have a small snack (10-15g carbs) without bolus post-exercise.",
      confidence: exerciseEntries.length > 4 ? 0.85 : 0.65,
      data: { count: exerciseEntries.length },
    };
  }

  return null;
}

/**
 * Analyze sick day patterns
 */
function analyzeSickDayPatterns(
  readings: CgmReading[],
  entries: LogEntry[],
): PatternResult | null {
  const sickEntries = entries.filter((e) => e.sickDay?.status === "sick");

  if (sickEntries.length === 0) return null;

  // Get readings during sick period
  let sickReadings: CgmReading[] = [];

  for (const entry of sickEntries) {
    const entryTime = getTimestamp(entry.timestamp);
    const dayStart = startOfDay(new Date(entryTime)).getTime();
    const dayEnd = endOfDay(new Date(entryTime)).getTime();

    const dayReadings = readings.filter((r) => {
      const ts = getTimestamp(r.timestamp);
      return ts >= dayStart && ts <= dayEnd;
    });

    sickReadings = [...sickReadings, ...dayReadings];
  }

  if (sickReadings.length < 10) return null;

  const avgSick = calculateAvg(sickReadings);

  return {
    type: "sick",
    severity: "action",
    title: "🤒 Sick Day Management Active",
    detail: `Sick day entries detected. Average BG during illness: ${avgSick.toFixed(1)} mmol/L. BG typically runs higher when unwell.`,
    dafneRecommendation:
      "Continue 2-hourly BG monitoring. Check ketones regularly. May need 10-20% extra on correction doses. Never skip basal insulin even if not eating.",
    confidence: 0.9,
    data: { avgBg: avgSick, count: sickEntries.length },
  };
}

/**
 * Analyze nighttime patterns (for basal assessment)
 */
function analyzeNighttimePatterns(
  readings: CgmReading[],
  _targetLow: number,
  targetHigh: number,
  daysAnalyzed: number,
): PatternResult | null {
  // Check overnight readings (midnight to 5am) when basal should be primary driver
  const overnightReadings = filterByTimePeriod(
    readings,
    TIME_PERIODS.overnight.start,
    TIME_PERIODS.overnight.end,
  );

  if (overnightReadings.length < 20) return null;

  const avgOvernight = calculateAvg(overnightReadings);
  const stdDev = calculateStdDev(overnightReadings);

  // Use episode-based analysis for nighttime lows
  const nightHypoEpisodes = groupIntoEpisodes(overnightReadings, 4.0, "below");
  const daysWithNightHypos = getDaysWithEpisodes(nightHypoEpisodes);

  // Nighttime hypos - basal too high
  if (nightHypoEpisodes.length >= 2 || daysWithNightHypos.size >= 2) {
    const avgDuration =
      nightHypoEpisodes.length > 0
        ? nightHypoEpisodes.reduce((sum, ep) => sum + ep.durationMinutes, 0) /
          nightHypoEpisodes.length
        : 0;

    return {
      type: "nighttime",
      severity: "action",
      title: "🌙 Nighttime Hypoglycemia Risk",
      detail: `${nightHypoEpisodes.length} overnight low episodes across ${daysWithNightHypos.size} of ${daysAnalyzed} nights${avgDuration > 0 ? `. Avg duration: ${formatDuration(Math.round(avgDuration))}` : ""}. This suggests basal insulin may be too high.`,
      dafneRecommendation:
        "Consider reducing evening basal insulin by 10-20%. If on twice-daily basal, focus on evening dose.",
      confidence: 0.85,
      data: { avgBg: avgOvernight, count: nightHypoEpisodes.length },
    };
  }

  // Check for consistent overnight highs using percentage of time above target
  const highReadings = overnightReadings.filter((r) => r.glucose > targetHigh);
  const highPercent = Math.round(
    (highReadings.length / overnightReadings.length) * 100,
  );

  // Consistent overnight highs - basal too low
  if (highPercent > 40 && avgOvernight > targetHigh) {
    return {
      type: "basal",
      severity: "warning",
      title: "📊 Basal May Need Increase",
      detail: `Average overnight BG is ${avgOvernight.toFixed(1)} mmol/L with ${highPercent}% of time above target. Stable overnight highs suggest insufficient basal.`,
      dafneRecommendation:
        "Consider increasing basal insulin by 1-2 units. Wait 2-3 days to see effect before further changes.",
      affectedPeriods: ["BT"],
      confidence: stdDev < 2 ? 0.85 : 0.65, // Higher confidence if readings are stable
      data: { avgBg: avgOvernight },
    };
  }

  return null;
}

/**
 * Analyze overall time in range
 */
function analyzeTir(
  readings: CgmReading[],
  targetLow: number,
  targetHigh: number,
): PatternResult | null {
  if (readings.length < 50) return null;

  const tir = calculateTir(readings, targetLow, targetHigh);
  const hypoPercent = Math.round(
    (countHypos(readings, targetLow) / readings.length) * 100,
  );
  const highPercent = Math.round(
    (countHighs(readings, targetHigh) / readings.length) * 100,
  );

  if (tir >= 70) {
    return {
      type: "tir",
      severity: "info",
      title: "✅ Good Time in Range",
      detail: `${tir}% of readings within target (${targetLow}-${targetHigh} mmol/L). ${hypoPercent}% low, ${highPercent}% high.`,
      dafneRecommendation:
        "Current insulin doses appear well-balanced. Continue monitoring for patterns and adjust as needed.",
      confidence: 0.95,
      data: { tir },
    };
  } else if (tir < 50) {
    return {
      type: "tir",
      severity: "action",
      title: "⚠️ Low Time in Range",
      detail: `Only ${tir}% of readings within target. ${hypoPercent}% low, ${highPercent}% high.`,
      dafneRecommendation:
        hypoPercent > highPercent
          ? "Focus on preventing lows first - consider reducing doses. Once lows are controlled, address highs."
          : "Review carb counting accuracy and consider tightening ratios. May also need basal adjustment.",
      confidence: 0.9,
      data: { tir },
    };
  }

  return null;
}

/**
 * Analyze glucose variability
 */
function analyzeVariability(readings: CgmReading[]): PatternResult | null {
  if (readings.length < 50) return null;

  const stdDev = calculateStdDev(readings);
  const cv = (stdDev / calculateAvg(readings)) * 100; // Coefficient of variation

  if (cv > 36) {
    // High variability threshold
    return {
      type: "variability",
      severity: "warning",
      title: "📉 High Glucose Variability",
      detail: `Glucose coefficient of variation is ${cv.toFixed(1)}% (target <36%). Large swings between highs and lows.`,
      dafneRecommendation:
        "High variability often indicates inconsistent carb counting or meal timing. Focus on eating similar portion sizes and consistent meal times.",
      confidence: 0.8,
    };
  }

  return null;
}

/**
 * Main pattern analysis function
 */
export function analyzePatterns(options: AnalysisOptions): PatternResult[] {
  const {
    cgmReadings,
    entries,
    daysToAnalyze = 7,
    targetLow = 4,
    targetHigh = 10,
  } = options;

  console.log("[PatternAnalysis] Starting analysis");
  console.log(
    `[PatternAnalysis] Input: ${cgmReadings.length} CGM readings, ${entries.length} entries, daysToAnalyze=${daysToAnalyze}`,
  );

  const patterns: PatternResult[] = [];

  if (cgmReadings.length < 20) {
    console.log("[PatternAnalysis] Not enough data (<20 readings)");
    return patterns; // Not enough data
  }

  // Find the date range of actual data (use data end, not "now")
  let maxTs = -Infinity;
  let minTs = Infinity;
  const now = Date.now();
  let maxReading: CgmReading | null = null;
  let futureCount = 0;

  for (const r of cgmReadings) {
    const ts = getTimestamp(r.timestamp);
    if (ts > maxTs) {
      maxTs = ts;
      maxReading = r;
    }
    if (ts < minTs) minTs = ts;
    if (ts > now) futureCount++;
  }

  console.log(
    `[PatternAnalysis] Raw data range: ${new Date(minTs).toISOString()} to ${new Date(maxTs).toISOString()}`,
  );
  console.log(`[PatternAnalysis] Current time: ${new Date(now).toISOString()}`);
  console.log(`[PatternAnalysis] Future readings count: ${futureCount}`);
  if (maxReading) {
    console.log(`[PatternAnalysis] Max reading details:`, {
      id: maxReading.id,
      timestamp: maxReading.timestamp,
      timestampType: typeof maxReading.timestamp,
      glucose: maxReading.glucose,
      source: maxReading.source,
    });
  }

  // Cap the end date to "now" if data contains future timestamps
  if (maxTs > now) {
    console.log(`[PatternAnalysis] Data contains future dates, capping to now`);
    maxTs = now;
  }

  // Filter to the most recent N days of actual data
  const dataEndDate = new Date(maxTs);
  const cutoff = subDays(dataEndDate, daysToAnalyze);
  console.log(
    `[PatternAnalysis] Data end: ${dataEndDate.toISOString()}, cutoff: ${cutoff.toISOString()}`,
  );

  const filteredReadings = cgmReadings.filter(
    (r) => getTimestamp(r.timestamp) >= cutoff.getTime(),
  );

  const filteredEntries = entries.filter(
    (e) => getTimestamp(e.timestamp) >= cutoff.getTime(),
  );

  console.log(
    `[PatternAnalysis] After filtering: ${filteredReadings.length} readings, ${filteredEntries.length} entries`,
  );

  if (filteredReadings.length < 20) {
    console.log("[PatternAnalysis] Not enough filtered data (<20 readings)");
    return patterns; // Not enough data after filtering
  }

  // Calculate actual days span in filtered data
  const actualDays = getActualDaysInData(filteredReadings);
  console.log(`[PatternAnalysis] Actual days in filtered data: ${actualDays}`);

  // Debug: count hypo readings
  const hypoCount = filteredReadings.filter(
    (r) => r.glucose < targetLow,
  ).length;
  console.log(`[PatternAnalysis] Hypo readings (<${targetLow}): ${hypoCount}`);

  // Run all pattern analyses
  const dawnPattern = analyzeDawnPhenomenon(filteredReadings, targetHigh);
  if (dawnPattern) patterns.push(dawnPattern);

  // Post-meal patterns for each main meal
  for (const period of ["B", "L", "EM"] as const) {
    const mealPattern = analyzePostMealPattern(
      filteredReadings,
      filteredEntries,
      period,
      targetHigh,
    );
    if (mealPattern) patterns.push(mealPattern);
  }

  const hypoPattern = analyzeHypoPatterns(filteredReadings, actualDays);
  if (hypoPattern) patterns.push(hypoPattern);

  const hyperPattern = analyzeHyperPatterns(
    filteredReadings,
    targetHigh,
    actualDays,
  );
  if (hyperPattern) patterns.push(hyperPattern);

  const exercisePattern = analyzeExerciseImpact(
    filteredReadings,
    filteredEntries,
  );
  if (exercisePattern) patterns.push(exercisePattern);

  const sickPattern = analyzeSickDayPatterns(filteredReadings, filteredEntries);
  if (sickPattern) patterns.push(sickPattern);

  const nightPattern = analyzeNighttimePatterns(
    filteredReadings,
    targetLow,
    targetHigh,
    actualDays,
  );
  if (nightPattern) patterns.push(nightPattern);

  const tirPattern = analyzeTir(filteredReadings, targetLow, targetHigh);
  if (tirPattern) patterns.push(tirPattern);

  const variabilityPattern = analyzeVariability(filteredReadings);
  if (variabilityPattern) patterns.push(variabilityPattern);

  // Sort by severity (action first, then warning, then info)
  const severityOrder: Record<PatternSeverity, number> = {
    action: 0,
    warning: 1,
    info: 2,
  };

  patterns.sort(
    (a, b) => severityOrder[a.severity] - severityOrder[b.severity],
  );

  return patterns;
}

/**
 * Generate predictions based on current state and historical patterns
 */
export function generatePredictions(
  currentEntries: LogEntry[], // Today's entries
  cgmReadings: CgmReading[], // Recent readings
  historicalPatterns: PatternResult[],
  currentPeriod: PeriodTag,
): PredictionResult[] {
  const predictions: PredictionResult[] = [];
  const now = new Date();

  // Calculate current IOB
  let currentIob = 0;
  for (const entry of currentEntries) {
    const qaTotal = getQATotal(entry);
    if (qaTotal) {
      const hoursElapsed =
        (now.getTime() - getTimestamp(entry.timestamp)) / (1000 * 60 * 60);
      if (hoursElapsed >= 0 && hoursElapsed <= 5) {
        currentIob += calculateIOB(qaTotal, hoursElapsed);
      }
    }
  }
  currentIob = Math.round(currentIob * 10) / 10;

  // Get latest BG
  const recentReadings = cgmReadings
    .filter((r) => {
      const age = (now.getTime() - getTimestamp(r.timestamp)) / (1000 * 60);
      return age <= 30; // Last 30 minutes
    })
    .sort((a, b) => getTimestamp(b.timestamp) - getTimestamp(a.timestamp));

  const latestBg = recentReadings.length > 0 ? recentReadings[0].glucose : null;

  // Stacking risk prediction
  if (currentIob > 2) {
    const lastDose = currentEntries
      .filter((e) => getQATotal(e))
      .sort((a, b) => getTimestamp(b.timestamp) - getTimestamp(a.timestamp))[0];

    if (lastDose) {
      const hoursSinceLast =
        (now.getTime() - getTimestamp(lastDose.timestamp)) / (1000 * 60 * 60);

      if (hoursSinceLast < 3) {
        predictions.push({
          type: "stacking",
          timeframe: "next 2 hours",
          likelihood: currentIob > 4 ? "high" : "medium",
          basedOn: `${currentIob}u IOB still active from ${Math.round(hoursSinceLast * 60)} minutes ago`,
          action:
            "Consider waiting or reducing correction dose to avoid insulin stacking",
          currentIob,
        });
      }
    }
  }

  // Hypo risk based on patterns
  const hypoPattern = historicalPatterns.find((p) => p.type === "hypo-pattern");
  if (hypoPattern && latestBg && latestBg < 6) {
    predictions.push({
      type: "hypo-risk",
      timeframe: "next 1-2 hours",
      likelihood: latestBg < 5 ? "high" : "medium",
      basedOn: `Current BG ${latestBg?.toFixed(1)} with historical hypo pattern. ${currentIob > 0 ? `${currentIob}u IOB active.` : ""}`,
      action:
        "Consider having a small snack or reducing next meal dose by 1-2 units",
      currentIob,
      predictedBg: latestBg ? latestBg - currentIob * 2 : undefined, // Rough estimate
    });
  }

  // Post-meal spike prediction
  const mealPattern = historicalPatterns.find(
    (p) => p.type === "post-meal" && p.affectedPeriods?.includes(currentPeriod),
  );
  if (mealPattern && latestBg && latestBg > 7) {
    predictions.push({
      type: "hyper-risk",
      timeframe: "next 1-3 hours",
      likelihood: mealPattern.severity === "action" ? "high" : "medium",
      basedOn: `Historical post-${PERIOD_TAG_INFO[currentPeriod].label.toLowerCase()} spikes detected. ${mealPattern.detail}`,
      action: mealPattern.dafneRecommendation,
    });
  }

  // Dawn phenomenon prediction (if evening/bedtime)
  if (
    (currentPeriod === "BT" || currentPeriod === "EM") &&
    historicalPatterns.some((p) => p.type === "dawn")
  ) {
    predictions.push({
      type: "hyper-risk",
      timeframe: "tomorrow morning (5-8 AM)",
      likelihood: "medium",
      basedOn: "Dawn phenomenon pattern detected in recent data",
      action:
        "If BG is in-range at bedtime, no action needed. If elevated, consider small correction.",
    });
  }

  return predictions;
}

/**
 * Get the current period based on time of day
 */
export function getCurrentPeriod(): PeriodTag {
  const hour = new Date().getHours();
  if (hour >= 0 && hour < 5) return "3AM";
  if (hour >= 5 && hour < 10) return "B";
  if (hour >= 10 && hour < 15) return "L";
  if (hour >= 15 && hour < 21) return "EM";
  return "BT";
}
